import type { LogLevel, LogMethod, LogRecord, LoggerSink, Logger, CreateLoggerOptions, ConsoleLoggerTarget, WritableLogStream } from './types/logger';

interface LoggerState {
    level: LogLevel;
}

const DEFAULT_LOG_CONTEXT = "global";
const NOOP: LogMethod = () => {};

export const LOG_LEVEL_HIERARCHY: Record<LogLevel, number> = {
    trace: 0,
    debug: 1,
    info: 2,
    warn: 3,
    error: 4,
    log: 5,
};

const defaultLoggerState: LoggerState = {
    level: "info",
};

class CommonLogger implements Logger {
    constructor(
        private readonly context: string,
        private readonly sink: LoggerSink,
        private readonly state: LoggerState,
    ) {}

    get level(): LogLevel {
        return this.state.level;
    }

    set level(level: LogLevel) {
        this.state.level = level;
    }

    child(context: string): Logger {
        return new CommonLogger(joinContexts(this.context, context), this.sink, this.state);
    }

    trace(...args: ReadonlyArray<unknown>): void {
        this.write("trace", args);
    }

    debug(...args: ReadonlyArray<unknown>): void {
        this.write("debug", args);
    }

    info(...args: ReadonlyArray<unknown>): void {
        this.write("info", args);
    }

    warn(...args: ReadonlyArray<unknown>): void {
        this.write("warn", args);
    }

    error(...args: ReadonlyArray<unknown>): void {
        this.write("error", args);
    }

    log(...args: ReadonlyArray<unknown>): void {
        this.write("log", args);
    }

    private write(level: LogLevel, args: ReadonlyArray<unknown>): void {
        if (!shouldLog(level, this.state.level)) {
            return;
        }

        this.sink.write({
            context: this.context,
            timestamp: new Date().toISOString(),
            level,
            message: formatMessage(args),
            data: [...args],
        });
    }
}

export function createLogger(context?: string): Logger;
export function createLogger(options?: CreateLoggerOptions): Logger;
export function createLogger(contextOrOptions?: string | CreateLoggerOptions): Logger {
    const options =
        typeof contextOrOptions === "string"
            ? { context: contextOrOptions }
            : (contextOrOptions ?? {});

    return new CommonLogger(
        normalizeContext(options.context),
        options.sink ?? createConsoleLoggerSink(),
        options.level ? { level: options.level } : defaultLoggerState,
    );
}

export function createConsoleLoggerSink(target?: ConsoleLoggerTarget): LoggerSink {
    const consoleTarget = target ?? (globalThis as { console?: ConsoleLoggerTarget }).console;

    return {
        write(record: LogRecord): void {
            resolveLogMethod(consoleTarget, record.level)(formatConsoleMessage(record));
        },
    };
}

export function createJsonStreamLoggerSink(stream: WritableLogStream): LoggerSink {
    return {
        write(record: LogRecord): void {
            stream.write(`${JSON.stringify(serializeLogRecord(record))}\n`);
        },
    };
}

export function createCompositeLoggerSink(...sinks: ReadonlyArray<LoggerSink | undefined>): LoggerSink {
    const activeSinks = sinks.filter((sink): sink is LoggerSink => typeof sink !== "undefined");

    return {
        write(record: LogRecord): void {
            for (const sink of activeSinks) {
                sink.write(record);
            }
        },
    };
}

function shouldLog(level: LogLevel, currentLevel: LogLevel): boolean {
    return LOG_LEVEL_HIERARCHY[level] >= LOG_LEVEL_HIERARCHY[currentLevel];
}

function resolveLogMethod(target: ConsoleLoggerTarget | undefined, level: LogLevel): LogMethod {
    const method = target?.[level] ?? target?.log;
    if (typeof method !== "function") {
        return NOOP;
    }

    return target ? method.bind(target) : method;
}

function formatConsoleMessage(record: LogRecord): string {
    return `[${record.context}] [${record.timestamp}] ${record.message}`;
}

function formatMessage(args: ReadonlyArray<unknown>): string {
    if (args.length === 0) {
        return "";
    }

    return args.map((value) => serializeLogValue(value)).join(" ");
}

function serializeLogRecord(record: LogRecord): Record<string, unknown> {
    return {
        context: record.context,
        timestamp: record.timestamp,
        level: record.level,
        message: record.message,
        data: record.data.map((value) => toJsonValue(value)),
    };
}

/**
 * Classification shared by `toJsonValue` and `serializeLogValue`: both walk
 * the same Error -> bigint/symbol -> undefined -> everything-else cascade,
 * they just render each case differently (structured JSON vs. a string).
 */
type ClassifiedLogValue =
    | { readonly kind: "error"; readonly value: Error }
    | { readonly kind: "bigint-or-symbol"; readonly value: bigint | symbol }
    | { readonly kind: "undefined" }
    | { readonly kind: "plain"; readonly value: unknown };

function classifyLogValue(value: unknown): ClassifiedLogValue {
    if (value instanceof Error) {
        return { kind: "error", value };
    }

    if (typeof value === "bigint" || typeof value === "symbol") {
        return { kind: "bigint-or-symbol", value };
    }

    if (typeof value === "undefined") {
        return { kind: "undefined" };
    }

    return { kind: "plain", value };
}

function toJsonValue(value: unknown): unknown {
    const classified = classifyLogValue(value);

    switch (classified.kind) {
        case "error":
            return {
                name: classified.value.name,
                message: classified.value.message,
                stack: classified.value.stack,
            };
        case "bigint-or-symbol":
            return classified.value.toString();
        case "undefined":
            return "undefined";
        case "plain": {
            const plainValue = classified.value;
            if (plainValue === null || typeof plainValue === "string" || typeof plainValue === "number" || typeof plainValue === "boolean") {
                return plainValue;
            }

            try {
                JSON.stringify(plainValue);
                return plainValue;
            } catch {
                return serializeLogValue(plainValue);
            }
        }
    }
}

function serializeLogValue(value: unknown): string {
    const classified = classifyLogValue(value);

    switch (classified.kind) {
        case "error":
            return classified.value.stack ?? `${classified.value.name}: ${classified.value.message}`;
        case "bigint-or-symbol":
            return classified.value.toString();
        case "undefined":
            return "undefined";
        case "plain": {
            const plainValue = classified.value;
            if (typeof plainValue === "string") {
                return plainValue;
            }

            try {
                const serialized = JSON.stringify(plainValue);
                return typeof serialized === "string" ? serialized : String(plainValue);
            } catch {
                return String(plainValue);
            }
        }
    }
}

function joinContexts(parentContext: string, childContext: string): string {
    const normalizedChildContext = childContext.trim();
    if (normalizedChildContext.length === 0) {
        return parentContext;
    }

    return `${parentContext}:${normalizedChildContext}`;
}

function normalizeContext(context: string | undefined): string {
    const normalizedContext = context?.trim();
    return normalizedContext && normalizedContext.length > 0
        ? normalizedContext
        : DEFAULT_LOG_CONTEXT;
}